import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { getSessionStore } from "app/utils/sessionStore";
import { getThemeColors, getUSANumberFormat } from "app/utils/utils";
import { getColorVariations } from "app/utils/colorUtils";
import type { BSKPAppendixData, NormalizedAppendixPolicy } from "./IRCFundingPage.adapters";

interface IRCBSKPAppendixPageProps {
  appendixData: BSKPAppendixData;
  pageNumber: string | number;
}

const fmt = (v: number) => `$${getUSANumberFormat(v, false)}`;

const PolicyTable = ({
  policies,
  hasLivingBenefit,
  colorVariations,
}: {
  policies: NormalizedAppendixPolicy[];
  hasLivingBenefit: boolean;
  colorVariations: string[];
}) => {
  const headers = [
    "Policy #",
    "Insurer",
    "Product",
    "Type",
    "Mode",
    "Method",
    "Status",
    "Effective Date",
    "Death Benefit",
    ...(hasLivingBenefit ? ["Living/CI Benefit"] : []),
    "Premium Amount",
    "Annual Premium",
    "Exp. (Yrs)",
  ];

  return (
    <table className="w-full border-collapse mb-4">
      <thead className="text-white">
        <tr>
          {headers.map((h, i) => (
            <th
              key={i}
              className="p-1 text-center pdf_table_border border-white font-semibold"
              style={{
                backgroundColor: colorVariations[i % colorVariations.length],
                fontSize: "7px",
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {policies.map((p, i) => (
          <tr key={i} style={{ backgroundColor: "#EEEEEE", color: "#000000", fontSize: "7px" }}>
            <td className="p-1 text-center pdf_table_border border-white">{p.policyNumber}</td>
            <td className="p-1 text-center pdf_table_border border-white">{p.insurerName}</td>
            <td className="p-1 text-center pdf_table_border border-white">{p.productName}</td>
            <td className="p-1 text-center pdf_table_border border-white">{p.productType}</td>
            <td className="p-1 text-center pdf_table_border border-white">{p.mode}</td>
            <td className="p-1 text-center pdf_table_border border-white">{p.method}</td>
            <td className="p-1 text-center pdf_table_border border-white capitalize">{p.status}</td>
            <td className="p-1 text-center pdf_table_border border-white">{p.effectiveDate}</td>
            <td className="p-1 text-center pdf_table_border border-white">{fmt(p.deathBenefit)}</td>
            {hasLivingBenefit && (
              <td className="p-1 text-center pdf_table_border border-white">
                {p.livingBenefit != null ? fmt(p.livingBenefit) : "—"}
              </td>
            )}
            <td className="p-1 text-center pdf_table_border border-white">
              {fmt(p.premiumAmount)}
            </td>
            <td className="p-1 text-center pdf_table_border border-white">
              {fmt(p.annualPremium)}
            </td>
            <td className="p-1 text-center pdf_table_border border-white">
              {p.expirationRemaining} {p.expirationRemaining === 1 ? "Yr" : "Yrs"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const IRCBSKPAppendixPage = ({ appendixData, pageNumber }: IRCBSKPAppendixPageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { secondaryColor } = getThemeColors(branding);
  const colorVariations = getColorVariations(secondaryColor, 13).reverse();

  const { lifePolicies, disabilityPolicies, hasLivingBenefit } = appendixData;

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[2em] w-full max-w-[56em] mx-auto p-[1.75em]">
        <h1 className="text-center text-2xl font-bold mb-5 conthrax-font interactive-proposal-text">
          Appendix
        </h1>

        {lifePolicies.length > 0 && (
          <>
            <p className="text-xs font-semibold mb-2 conthrax-font" style={{ color: "#495057" }}>
              Life Policies
            </p>
            <PolicyTable
              policies={lifePolicies}
              hasLivingBenefit={hasLivingBenefit}
              colorVariations={colorVariations}
            />
          </>
        )}

        {disabilityPolicies.length > 0 && (
          <>
            <p className="text-xs font-semibold mb-2 conthrax-font" style={{ color: "#495057" }}>
              Disability Policies
            </p>
            <PolicyTable
              policies={disabilityPolicies}
              hasLivingBenefit={false}
              colorVariations={colorVariations}
            />
          </>
        )}
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCBSKPAppendixPage;
